import React, {
  FunctionComponent,
  ChangeEvent,
  useState,
  useEffect,
  useRef,
  useContext,
} from "react";
import { EnrollmentFormProps2022 } from "./../EnrollmentFormPage";
import { FocusableHeader } from "../../../components";
import BackNextButtons from "./../BackNextButtons";
import {
  useTranslate,
  sendTealiumEvent,
  EnrollmentPages,
  getValidationObject,
  limitInputLength,
  isMedicareAdvantageType,
  isPdPlanType,
  isMaPlanType,
  isMapdPlanType,
} from "../../../helpers";
import {
  Choice,
  Alert,
  ChoiceList,
  Dropdown,
  TextField,
} from "@cmsgov/ds-medicare-gov";
import PlanInfo from "./../PlanInfo";
import states from "./../states";
import PhoneNumberInput from "./../PhoneNumberInput";
import { removeExtraWhiteSpace } from "../../../helpers/enrollmentFormHelpers";
import { FormattedMessage } from "react-intl-phraseapp";
import { AppStore } from "../../../app/store";
import { getPlanName } from "../../../helpers/planHelpers";

const Agreement: FunctionComponent<EnrollmentFormProps2022> = ({
  enrollData,
  setEnrollData,
  previousPage,
  nextPage,
  plan,
  inReview,
}) => {
  const t = useTranslate();
  const {
    state: { language },
  } = useContext(AppStore.AppContext);
  /* istanbul ignore next: built-in functionality */
  const [isValidForm, setIsValidForm] = useState(false);
  /* istanbul ignore next: built-in functionality */
  const [submitted, setSubmitted] = useState(false);
  /* istanbul ignore next: built-in functionality */
  const [
    agreeToContractErrorMessage,
    setAgreeToContractErrorMessage,
  ] = useState<string | undefined>(undefined);
  /* istanbul ignore next: built-in functionality */
  const [isEnrolleeErrorMessage, setIsEnrolleeErrorMessage] = useState<
    string | undefined
  >(undefined);
  /* istanbul ignore next: built-in functionality */
  const [
    authorizedRepresentativeNameErrorMessage,
    setauthorizedRepresentativeNameErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  /* istanbul ignore next: built-in functionality */
  const [
    authorizedRepresentativeRelationshipErrorMessage,
    setauthorizedRepresentativeRelationshipErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  /* istanbul ignore next: built-in functionality */
  const [
    authorizedRepresentativeAddressLine1ErrorMessage,
    setauthorizedRepresentativeAddressLine1ErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  /* istanbul ignore next: built-in functionality */
  const [
    authorizedRepresentativeCityErrorMessage,
    setauthorizedRepresentativeCityErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  /* istanbul ignore next: built-in functionality */
  const [
    authorizedRepresentativeStateErrorMessage,
    setauthorizedRepresentativeStateErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  /* istanbul ignore next: built-in functionality */
  const [
    authorizedRepresentativeZipErrorMessage,
    setauthorizedRepresentativeZipErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  /* istanbul ignore next: built-in functionality */
  const [
    authorizedRepresentativePhoneErrorMessage,
    setauthorizedRepresentativePhoneErrorMessageErrorMessage,
  ] = useState<string | undefined>(undefined);
  const alertDiv = useRef<HTMLDivElement>(null);

  const validateForm = (): void => {
    const formStatus = getValidationObject(
      enrollData,
      plan,
      EnrollmentPages.Agreement
    );

    setIsValidForm(formStatus.isValid);

    if (submitted) {
      if (formStatus.agreeToContractValidity) {
        setAgreeToContractErrorMessage(undefined);
      } else {
        setAgreeToContractErrorMessage(
          t("enroll_form.agreement.click_box_to_continue")
        );
      }

      if (formStatus.isEnrolleeValidity) {
        setIsEnrolleeErrorMessage(undefined);
      } else {
        setIsEnrolleeErrorMessage(
          t("enroll_form.click_button_to_choose_option")
        );
      }

      if (formStatus.authorizedRepresentativeNameValidity) {
        setauthorizedRepresentativeNameErrorMessageErrorMessage(undefined);
      } else {
        setauthorizedRepresentativeNameErrorMessageErrorMessage(
          t("enroll_form.authorized_representative.name.error")
        );
      }
      if (formStatus.authorizedRepresentativeRelationshipValidity) {
        setauthorizedRepresentativeRelationshipErrorMessageErrorMessage(
          undefined
        );
      } else {
        setauthorizedRepresentativeRelationshipErrorMessageErrorMessage(
          t("enroll_form.authorized_representative.relationship.error")
        );
      }
      if (formStatus.authorizedRepresentativeAddressLine1Validity) {
        setauthorizedRepresentativeAddressLine1ErrorMessageErrorMessage(
          undefined
        );
      } else {
        setauthorizedRepresentativeAddressLine1ErrorMessageErrorMessage(
          t("enroll_form.address.line_1.error")
        );
      }
      if (formStatus.authorizedRepresentativeCityValidity) {
        setauthorizedRepresentativeCityErrorMessageErrorMessage(undefined);
      } else {
        setauthorizedRepresentativeCityErrorMessageErrorMessage(
          t("enroll_form.address.city.error")
        );
      }
      if (formStatus.authorizedRepresentativeStateValidity) {
        setauthorizedRepresentativeStateErrorMessageErrorMessage(undefined);
      } else {
        setauthorizedRepresentativeStateErrorMessageErrorMessage(
          t("enroll_form.address.state.error")
        );
      }
      if (formStatus.authorizedRepresentativeZipValidity) {
        setauthorizedRepresentativeZipErrorMessageErrorMessage(undefined);
      } else {
        setauthorizedRepresentativeZipErrorMessageErrorMessage(
          t("enroll_form.address.zip.error")
        );
      }
      if (formStatus.authorizedRepresentativePhoneValidity) {
        setauthorizedRepresentativePhoneErrorMessageErrorMessage(undefined);
      } else {
        setauthorizedRepresentativePhoneErrorMessageErrorMessage(
          t("enroll_form.address.phone.error")
        );
      }
    }
  };

  const submit = (): void => {
    setSubmitted(true);

    if (isValidForm) {
      /* istanbul ignore next: tested elsewhere */
      nextPage();
    } else {
      /* istanbul ignore next: tested elsewhere */
      validateForm();
    }
  };

  useEffect(() => {
    if (submitted && !isValidForm) {
      // istanbul ignore next: not testing
      if (alertDiv.current) {
        // istanbul ignore next: not testing
        alertDiv.current.scrollIntoView();
      }
    }
  }, [submitted]);

  useEffect(() => {
    validateForm();
  }, [enrollData, submitted]);

  const isMa = isMaPlanType(plan.plan_type);
  const isMapd = isMapdPlanType(plan.plan_type);
  const isMaMapd = isMedicareAdvantageType(plan.plan_type);
  const isPdp = isPdPlanType(plan.plan_type);
  const planName = getPlanName(plan, language);

  return (
    <div>
      <FocusableHeader
        className="EnrollmentFormPage__header"
        text={t("enrollment_form.plan_agreement")}
      />

      <div className="ds-u-measure--wide">
        <PlanInfo plan={plan} />

        <span className="ds-u-visibility--screen-reader">
          {t("sr_only_additional_questions_may_appear")}
        </span>

        <div ref={alertDiv}>
          {!isValidForm && submitted && (
            <Alert
              className="ds-u-margin-top--2"
              heading={t("enroll_form.needs_your_attention")}
              variation="error"
            >
              {t("enroll_form.complete_required")}
            </Alert>
          )}
        </div>

        <section>
          <h2 className="ds-u-margin-top--3">
            {t("enroll.agreement.2021.read_and_sign")}
          </h2>
          <h3>{t("enroll.agreement.2021.understand_the_following")}</h3>
          <ul className="EnrollmentFormPage__agreementList">
            {isMaMapd && (
              <li>
                <FormattedMessage
                  id="enroll.agreement.2021.keep_part_a_and_b"
                  values={{ planName }}
                />
              </li>
            )}
            {isPdp && (
              <li>
                <FormattedMessage
                  id="enroll.agreement.2021.keep_part_a_or_b"
                  values={{ planName }}
                />
              </li>
            )}
            <li>
              <FormattedMessage
                id="enroll.agreement.2021.will_share_with_medicare"
                values={{ planName }}
              />
            </li>
            <li>{t("enroll.agreement.2021.info_correct")}</li>
            <li>{t("enroll.agreement.2021.not_covered_outside_country")}</li>
            {isMapd && (
              <li>
                <FormattedMessage
                  id="enroll.agreement.2021.health_and_drug_eoc"
                  values={{ planName }}
                />
              </li>
            )}
            {isMa && (
              <li>
                <FormattedMessage
                  id="enroll.agreement.2021.health_eoc"
                  values={{ planName }}
                />
              </li>
            )}
          </ul>
          <p>{t("enroll.agreement.2021.checking_box_understand")}</p>
          <p>{t("enroll.agreement.2021.if_person_legally_authorized")}</p>
          <ol className="EnrollmentFormPage__agreementList">
            <li>{t("enroll.agreement.2021.authorized_to_complete")}</li>
            <li>{t("enroll.agreement.2021.legal_doc_authority")}</li>
          </ol>

          {agreeToContractErrorMessage && (
            <span
              className="ds-c-field__hint ds-u-color--error"
              role="alert"
              data-testid="agreeToContractError"
            >
              {agreeToContractErrorMessage}
            </span>
          )}
          <Choice
            type="checkbox"
            name="agreeToContract"
            checked={enrollData.agreeToContract}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              /* istanbul ignore next: tested elsewhere */
              setEnrollData({
                ...enrollData,
                agreeToContract: e.target.checked,
              });
            }}
            value="agreed"
          >
            {t("enroll.agreement.2021.read_and_agree")}
          </Choice>

          <ChoiceList
            type="radio"
            choices={[
              {
                label: t("enroll.agreement.2021.i_am_the_person_joining"),
                value: "yes",
                checked: enrollData.isEnrollee,
              },
              {
                label: t(
                  "enroll.agreement.2021.i_am_legally_joining_on_behalf"
                ),
                value: "no",
                checked: enrollData.isEnrollee === false,
              },
            ]}
            errorMessage={isEnrolleeErrorMessage}
            label={
              <h3 className="mct-u-text-transform--none">
                {t("enroll.agreement.2021.who_is_joining")}
              </h3>
            }
            name="isEnrollee"
            onChange={(e: ChangeEvent<HTMLSelectElement>): void => {
              /* istanbul ignore next: built-in functionality */
              const isEnrollee = e.currentTarget.value === "yes";
              // istanbul ignore next: tested elsewhere
              sendTealiumEvent(
                "Plan Enroll - Plan Agreement",
                `Relationship ${
                  isEnrollee
                    ? "person listed on form"
                    : "authorized representative"
                }`
              );
              /* istanbul ignore next: tested elsewhere */
              setEnrollData({
                ...enrollData,
                isEnrollee,
              });
            }}
          />
          {!enrollData.isEnrollee && enrollData.isEnrollee !== undefined && (
            <>
              <TextField
                name="authorizedRepresentativeName"
                label={t("enroll_form.authorized_representative.name")}
                value={enrollData.authorizedRepresentative.name}
                errorMessage={authorizedRepresentativeNameErrorMessage}
                onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                  /* istanbul ignore next: tested elsewhere */
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      name: e.target.value,
                    },
                  });
                }}
                onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      name: removeExtraWhiteSpace(e.target.value),
                    },
                  });
                }}
              />
              <TextField
                name="authorizedRepresentativeRelationship"
                label={t("enroll.agreement.2021.relationship")}
                value={enrollData.authorizedRepresentative.relationship}
                errorMessage={authorizedRepresentativeRelationshipErrorMessage}
                onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                  /* istanbul ignore next: tested elsewhere */
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      relationship: e.target.value,
                    },
                  });
                }}
                onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      relationship: removeExtraWhiteSpace(e.target.value),
                    },
                  });
                }}
              />
              <TextField
                name="authorizedRepresentativeAddressLine1"
                label={t("enroll.agreement.2021.address")}
                value={enrollData.authorizedRepresentative.addressLine1}
                errorMessage={authorizedRepresentativeAddressLine1ErrorMessage}
                onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                  /* istanbul ignore next: tested elsewhere */
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      addressLine1: e.target.value,
                    },
                  });
                }}
                onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      addressLine1: removeExtraWhiteSpace(e.target.value),
                    },
                  });
                }}
              />
              <TextField
                name="authorizedRepresentativeCity"
                label={t("enroll_form.address.city")}
                value={enrollData.authorizedRepresentative.city}
                errorMessage={authorizedRepresentativeCityErrorMessage}
                onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                  /* istanbul ignore next: tested elsewhere */
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      city: e.target.value,
                    },
                  });
                }}
                onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      city: removeExtraWhiteSpace(e.target.value),
                    },
                  });
                }}
              />
              <Dropdown
                options={[
                  {
                    name: t("enroll.agreement.2021.select_your_state"),
                    fipsCode: "",
                  },
                  ...states,
                ].map(state => {
                  return { label: state.name, value: state.fipsCode };
                })}
                value={
                  enrollData.authorizedRepresentative
                    ? enrollData.authorizedRepresentative.state
                    : ""
                }
                errorMessage={authorizedRepresentativeStateErrorMessage}
                label={t("enroll_form.address.state")}
                name="authorizedRepresentativeState"
                onChange={(e: ChangeEvent<HTMLSelectElement>): void => {
                  /* istanbul ignore next: tested elsewhere */
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      state: e.currentTarget.value,
                    },
                  });
                }}
              />
              <TextField
                name="authorizedRepresentativeZip"
                label={t("enroll_form.address.zip")}
                size="medium"
                value={enrollData.authorizedRepresentative.zip}
                errorMessage={authorizedRepresentativeZipErrorMessage}
                fieldClassName="ds-c-field--zipCode"
                onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                  /* istanbul ignore next: tested elsewhere */
                  limitInputLength("zipCode", 5);
                  /* istanbul ignore next: tested elsewhere */
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      zip: e.target.value,
                    },
                  });
                }}
              />
              <PhoneNumberInput
                phoneNumber={enrollData.authorizedRepresentative.phone}
                name="authorizedRepresentativePhone"
                errorMessage={authorizedRepresentativePhoneErrorMessage}
                label={t("enroll_form.phone_number")}
                size="medium"
                onChange={(authorizedRepresentativePhone): void => {
                  setEnrollData({
                    ...enrollData,
                    authorizedRepresentative: {
                      ...enrollData.authorizedRepresentative,
                      phone: authorizedRepresentativePhone,
                    },
                  });
                }}
              />
            </>
          )}
        </section>

        <BackNextButtons
          inReview={inReview}
          nextPage={submit}
          previousPage={previousPage}
        />
      </div>
    </div>
  );
};

export default Agreement;
